import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';

async function deleteMenuItemIngredientsByCompany(companyId: string): Promise<void> {
  const { data: menuItems } = await supabase
    .from('menu_items')
    .select('id')
    .eq('company_id', companyId);

  if (menuItems && menuItems.length > 0) {
    const ids = menuItems.map(m => m.id);
    const { error } = await supabase
      .from('menu_item_ingredients')
      .delete()
      .in('menu_item_id', ids);
    if (error) throw new Error(`Failed to delete menu item ingredients: ${error.message}`);

    // Also clean up combo items
    const { error: comboError } = await supabase
      .from('menu_item_combo_items')
      .delete()
      .in('menu_item_id', ids);
    if (comboError) console.warn('Error deleting menu item combo items:', comboError);
  }
}

async function deletePrepItemIngredientsByCompany(companyId: string): Promise<void> {
  const { data: prepItems } = await supabase
    .from('prep_items')
    .select('id')
    .eq('company_id', companyId);

  if (prepItems && prepItems.length > 0) {
    const ids = prepItems.map(p => p.id);
    const { error } = await supabase
      .from('prep_item_ingredients')
      .delete()
      .in('prep_item_id', ids);
    if (error) throw new Error(`Failed to delete prep item ingredients: ${error.message}`);
  }
}

export async function resetInventoryData(companyId: string): Promise<void> {
  // Delete join table rows first (they reference ingredients via FK)
  await deleteMenuItemIngredientsByCompany(companyId);
  await deletePrepItemIngredientsByCompany(companyId);

  const { error: ingredientsError } = await supabase
    .from('ingredients')
    .delete()
    .eq('company_id', companyId);
  if (ingredientsError) throw new Error(`Failed to delete ingredients: ${ingredientsError.message}`);

  await resetOnboardingSection(companyId, 'inventory_list_setup');
}

export async function resetCOGSVendorData(companyId: string): Promise<void> {
  const { error: mappingsError } = await supabase
    .from('vendor_ingredient_mappings')
    .delete()
    .eq('company_id', companyId);
  if (mappingsError) throw new Error(`Failed to delete vendor mappings: ${mappingsError.message}`);

  const { error: clustersError } = await supabase
    .from('invoice_line_item_clusters')
    .delete()
    .eq('company_id', companyId);
  if (clustersError) throw new Error(`Failed to delete invoice clusters: ${clustersError.message}`);

  const { error: samplesError } = await supabase
    .from('vendor_invoice_training_samples')
    .delete()
    .eq('company_id', companyId);
  if (samplesError) console.warn('Error deleting training samples:', samplesError);

  const { error: invoicesError } = await supabase
    .from('vendor_invoice_training')
    .delete()
    .eq('company_id', companyId);
  if (invoicesError) throw new Error(`Failed to delete vendor invoices: ${invoicesError.message}`);

  const { error: progressError } = await supabase
    .from('vendor_training_progress')
    .delete()
    .eq('company_id', companyId);
  if (progressError) throw new Error(`Failed to delete training progress: ${progressError.message}`);

  const { error: vendorsError } = await supabase
    .from('vendors')
    .delete()
    .eq('company_id', companyId);
  if (vendorsError) throw new Error(`Failed to delete vendors: ${vendorsError.message}`);

  await resetOnboardingSection(companyId, 'cogs_vendor_training');
  await resetOnboardingSection(companyId, 'vendors');
}

export async function resetPrepMenuData(companyId: string): Promise<void> {
  await deleteMenuItemIngredientsByCompany(companyId);

  const { error: menuError } = await supabase
    .from('menu_items')
    .delete()
    .eq('company_id', companyId);
  if (menuError) throw new Error(`Failed to delete menu items: ${menuError.message}`);

  await deletePrepItemIngredientsByCompany(companyId);

  const { error: prepError } = await supabase
    .from('prep_items')
    .delete()
    .eq('company_id', companyId);
  if (prepError) throw new Error(`Failed to delete prep items: ${prepError.message}`);

  await resetOnboardingSection(companyId, 'prep_menu_recipes');
}

async function resetOnboardingSection(companyId: string, sectionKey: string): Promise<void> {
  const { data } = await supabase
    .from('onboarding_progress')
    .select('sections_completed')
    .eq('company_id', companyId)
    .single();

  if (!data) return;

  const sections = (data.sections_completed as Record<string, any>) || {};

  if (sectionKey === 'cogs_vendor_training') {
    if (sections.phase2) {
      delete sections.phase2.cogs_vendor_training;
    }
  }

  delete sections[sectionKey];

  await supabase
    .from('onboarding_progress')
    .update({
      sections_completed: sections,
      last_activity: new Date().toISOString(),
    })
    .eq('company_id', companyId);
}

export type ResetModuleId = 'inventory_list_setup' | 'cogs_vendor_training' | 'prep_menu_recipes';

const resetFunctions: Record<ResetModuleId, (companyId: string) => Promise<void>> = {
  inventory_list_setup: resetInventoryData,
  cogs_vendor_training: resetCOGSVendorData,
  prep_menu_recipes: resetPrepMenuData,
};

export const MODULE_RESET_CONFIG: Record<ResetModuleId, { label: string; description: string }> = {
  inventory_list_setup: {
    label: 'Inventory List',
    description: 'This will permanently delete all ingredients and their associations with prep items and menu items.',
  },
  cogs_vendor_training: {
    label: 'COGS Vendor Training',
    description: 'This will permanently delete all vendors, uploaded invoices, AI training data, and ingredient mappings.',
  },
  prep_menu_recipes: {
    label: 'Prep & Menu Recipes',
    description: 'This will permanently delete all prep items, menu items, and their ingredient recipes.',
  },
};

export async function resetModuleData(moduleId: ResetModuleId): Promise<void> {
  const companyId = await getCurrentUserCompanyId();
  if (!companyId) throw new Error('Company ID not found');

  const resetFn = resetFunctions[moduleId];
  if (!resetFn) throw new Error(`No reset function for module: ${moduleId}`);

  await resetFn(companyId);
}
